import { useMemo } from 'react';
import { Text } from '@tremor/react';
import { t } from '../lib/i18n';
import type { SalesTicket } from '../api/types';
import ToggleableChart, { type Slice } from './ToggleableChart';

interface Props {
  tickets: SalesTicket[];
  loading: boolean;
  /** Click a "Por línea" bar/slice to drill into top parts for that line. */
  onLineClick?: (line: string) => void;
}

/**
 * Renders the 4-chart breakdown (by tkt.type, by partf.line, by salesperson,
 * by paid/pending) for a pre-filtered set of tickets. Reused by HourDrilldown
 * and StoreDrilldown modals — both fetch /api/sales themselves and hand a
 * narrowed list down here.
 */
export default function SalesAggregationView({ tickets, loading, onLineClick }: Props) {
  // Aggregation #1 — by tkt.type. Codes 0-3 only because /api/sales already
  // filters out type 4 (ROA payment) and 9 (Factura Global).
  const byType: Slice[] = useMemo(() => {
    const buckets = new Map<string, number>();
    const labelFor = (type: number) => {
      switch (type) {
        case 0: return t.hourDrill.typeCredit;
        case 1: return t.hourDrill.typeCreditDebit;
        case 2: return t.hourDrill.typeCash;
        case 3: return t.hourDrill.typeCashDebit;
        default: return String(type);
      }
    };
    for (const r of tickets) {
      const k = labelFor(r.type);
      buckets.set(k, (buckets.get(k) ?? 0) + r.total_mxn);
    }
    return [...buckets].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  }, [tickets]);

  // Aggregation #2 — by partf.line. Sum each titm's extended price (sell * qty)
  // into its line bucket.
  const byLine: Slice[] = useMemo(() => {
    const buckets = new Map<string, number>();
    for (const r of tickets) {
      for (const li of r.lines) {
        const ext = li.sell_mxn * li.qty_sold;
        buckets.set(li.line, (buckets.get(li.line) ?? 0) + ext);
      }
    }
    return [...buckets]
      .map(([label, value]) => ({ label, value }))
      .filter((s) => s.value !== 0)
      .sort((a, b) => b.value - a.value);
  }, [tickets]);

  // Aggregation #3 — by salesperson (slid). Empty slid → "Sin vendedor".
  const bySlid: Slice[] = useMemo(() => {
    const buckets = new Map<string, number>();
    for (const r of tickets) {
      const k = r.slid && r.slid.trim() !== '' ? r.slid : t.hourDrill.noSlid;
      buckets.set(k, (buckets.get(k) ?? 0) + r.total_mxn);
    }
    return [...buckets].map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value);
  }, [tickets]);

  // Aggregation #4 — paid vs pending. Cash always paid (cash in hand). Credit
  // splits into paid (balance ≤ 0) and pending (balance > 0).
  const byPaid: Slice[] = useMemo(() => {
    let paid = 0, pending = 0, cash = 0;
    for (const r of tickets) {
      const isCredit = r.type === 0 || r.type === 1;
      if (!isCredit) {
        cash += r.total_mxn;
      } else if ((r.balance_mxn ?? 0) > 0.001) {
        pending += r.balance_mxn ?? 0;
        paid += r.total_mxn - (r.balance_mxn ?? 0);
      } else {
        paid += r.total_mxn;
      }
    }
    const out: Slice[] = [];
    if (cash > 0)    out.push({ label: t.hourDrill.cashAlwaysPaid, value: cash });
    if (paid > 0)    out.push({ label: t.hourDrill.paid,           value: paid });
    if (pending > 0) out.push({ label: t.hourDrill.unpaid,         value: pending });
    return out;
  }, [tickets]);

  if (loading) return <Text className="mt-4">{t.common.loading}</Text>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
      <ToggleableChart title={t.hourDrill.byType}        data={byType} initial="pie" />
      <ToggleableChart title={t.hourDrill.byLine}        data={byLine} initial="bar" onSliceClick={onLineClick} />
      <ToggleableChart title={t.hourDrill.bySalesperson} data={bySlid} initial="bar" />
      <ToggleableChart
        title={t.hourDrill.byPaid}
        data={byPaid}
        initial="pie"
        colors={['cyan', 'emerald', 'red']}
      />
    </div>
  );
}
